import { driver } from "driver.js";
import { useCallback, useEffect, useMemo } from "react";
import { present } from "../../../lib/util/present";
import { addIncident } from "../../reduxToolkit/incidentsSlice";
import { addExpense, updateExpense } from "../../reduxToolkit/expensesSlice";
import { v4 as uuidv4 } from 'uuid';
import { store } from "../../reduxToolkit/store"
import { formatDateInTimeZone } from "../../../lib/formatDateInTimeZone";
import { NavigateFunction, useNavigate } from "react-router";
import { selectSharingMinistry } from "../../reduxToolkit/selectors/sharingMinistry";
import { getTerminology } from "../../../lib/terminology";
import { selectHasFeature } from "../../providers/featureFlags";

import "driver.js/dist/driver.css";

// Step 1 anchors on the Expenses draft empty-state "link a card" CTA. That CTA
// lives behind DraftExpensesEmptyState's async /backend/plaid/items fetch (a
// loading bar shows until state.plaid.loaded flips true), so on a fresh signup
// the element can appear well after navigation. We wait for it before driving.
const FIRST_STEP_ANCHOR = '.expenses__draft-empty .btn-primary';

// Poll for `selector` to appear in the DOM, resolving as soon as it does. Caps
// the wait (maxMs) so the tour still starts when the anchor never renders — e.g.
// a Help re-run by a user who already linked an account, where driver.js falls
// back to a centered popover.
function waitForElement(selector: string, maxMs = 5000, stepMs = 100): Promise<void> {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      if (document.querySelector(selector) || Date.now() - start >= maxMs) {
        resolve();
        return;
      }
      setTimeout(check, stepMs);
    };
    check();
  });
}

// The tour walks the "happy path" — link a card, a draft expense appears, assign
// it to an incident/need, attach an itemized bill, submit, and meet Alice.
//
// A brand-new user has no linked card and no draft expense, so we "puppet" the
// demo: instead of opening Plaid (whose modal lives outside our DOM), we
// fabricate a demo draft expense in Redux and, a step later, assign it to a demo
// incident. These optimistic local records mirror what the real flows create;
// they're the same technique the previous expense-creation tour used.
//
// Anchoring notes:
//  - The tour runs entirely on the Expenses screen for its first two steps: the
//    "link a card" step anchors on the Expenses draft empty-state CTA, then the
//    draft expense appears in place without leaving the screen. That CTA is
//    present on the fresh-signup path this tour is built for (no drafts, no
//    linked account yet). If the tour is re-run from Help by a user who already
//    linked an account, the CTA may be absent and driver.js simply centers the
//    first popover instead of highlighting it — the rest of the flow is
//    unaffected because the demo draft is fabricated regardless.
//  - After assigning the demo expense we navigate away from the edit form rather
//    than lingering on it, because the edit form's incident picker only syncs its
//    selection at mount and wouldn't reflect the Redux assignment in place.
function createDriver(navigate: NavigateFunction) {
  const initialState = store.getState();
  const ministry = selectSharingMinistry(initialState);
  const terms = getTerminology(ministry);
  const aliceEnabled = selectHasFeature(initialState.membership, 'alice_ai');

  // Ids for the demo records, created as the user advances through the tour.
  let incidentId: string | null = null;
  let expenseId: string | null = null;

  // Fabricate the draft expense that "appears" after linking a card: a Plaid-
  // sourced draft with a bank-tagged charge line. We only add the expense (never
  // touch the user's real plaid items) so a returning user's linked accounts are
  // left untouched.
  function createDemoDraft() {
    const now = new Date().toISOString();
    const today = formatDateInTimeZone(new Date(), { format: 'yyyy-MM-dd' });
    const state = store.getState();
    const profile = state.membership.profile;
    const patient_dob = profile?.date_of_birth || '1980-01-01';
    const patient_name = profile?.full_name || 'Jamie Doe';

    expenseId = uuidv4();

    store.dispatch(addExpense({
      id: expenseId,
      created_at: now,
      updated_at: now,
      date: today,
      created_by_user_id: state.membership.userId,
      membership_id: state.membership.membershipId,
      patient_dob,
      patient_name,
      provider: 'General Hospital (demo)',
      listedAmount: '1850.00',
      paidAmount: '1850.00',
      is_draft: true,
      // Deliberately no plaid_transaction_id, here or on the charge line below.
      // addExpense goes through the sync engine, so this demo draft is really
      // inserted into `expenses` — and expenses.plaid_transaction_id is a FK to
      // plaid_transactions, a table the tour never writes to. A fabricated id
      // fails the insert with 23503, and because failed sync actions stay
      // pending it is retried forever, wedging the whole sync-up loop.
      // payment_history is jsonb with no FK, and source: 'plaid' alone is enough
      // for isPlaidCharge, so the bank charge chip still renders.
      payment_history: {
        _version: '2024-12-24',
        payments: [{
          date: today,
          amount: '1850.00',
          source: 'plaid',
          institution_name: 'Bank of America',
          account_mask: '4821',
        }],
      },
    }))
  }

  // Assign the demo draft to a demo incident: create the incident and clear the
  // draft flag on the expense (which is what saving the assignment does).
  function assignDemoToIncident() {
    const now = new Date().toISOString();
    const state = store.getState();
    const profile = state.membership.profile;
    const patient_dob = profile?.date_of_birth || '1980-01-01';
    const patient_name = profile?.full_name || 'Jamie Doe';

    incidentId = uuidv4();
    store.dispatch(addIncident({
      id: incidentId,
      created_at: now,
      updated_at: now,
      created_by_user_id: state.membership.userId,
      membership_id: state.membership.membershipId,
      start_date: now,
      description: 'Broken Leg (demo incident)',
      patient_dob,
      patient_name,
    }))
    if (expenseId) {
      store.dispatch(updateExpense({
        id: expenseId,
        updated_at: now,
        incident_id: incidentId,
        is_draft: false,
      }))
    }
  }

  const submitTitle = (() => {
    if (ministry === 'Sedera') return "When you're ready, submit to Sedera";
    if (ministry === 'SMI') return "When you're ready, submit to Samaritan Ministries";
    if (ministry) return "When you're ready, submit to CHM";
    return `When you're ready, submit your ${terms.incident}`;
  })();

  const driverObj = driver({
    showProgress: true,
    steps: [
      {
        // The Expenses draft empty-state CTA that starts the Plaid link flow.
        element: FIRST_STEP_ANCHOR,
        popover: {
          title: 'Start by linking a card',
          description: 'Connect a bank or HSA card and we automatically import your medical charges as draft expenses — no manual entry.',
          showButtons: ['next', 'close'],
          onNextClick: () => {
            // We don't open Plaid during the tour; instead we show what happens
            // right after linking: a draft expense appears in place, right here on
            // the Expenses screen — no navigation needed.
            createDemoDraft();
            setTimeout(() => driverObj.moveNext(), 300);
          }
        }
      },
      {
        element: '.triage-expense-row--draft',
        popover: {
          title: 'Your charge becomes a draft',
          description: 'This draft was imported automatically from a card charge. Review it, then assign it so we can track your reimbursement.',
          showButtons: ['next', 'close'],
          onNextClick: () => {
            if (expenseId) navigate(`/expenses/${expenseId}/edit`);
            setTimeout(() => driverObj.moveNext(), 300);
          }
        }
      },
      {
        element: '.incident-input-group',
        popover: {
          title: `Assign it to ${terms.incident === 'need' ? 'a need' : 'an incident'}`,
          description: `Group related expenses under a single ${terms.incident} — that's what you submit for reimbursement.`,
          showButtons: ['next', 'close'],
          onNextClick: () => {
            // Assign, then move off the edit form: its incident picker only syncs
            // its selection at mount, so it wouldn't reflect this change in place.
            assignDemoToIncident();
            navigate('/receipts');
            setTimeout(() => driverObj.moveNext(), 300);
          }
        }
      },
      {
        element: '.receipt-library',
        popover: {
          title: 'Attach an itemized bill',
          description: 'Upload or snap an itemized bill and we extract the details automatically to support your reimbursement.',
          showButtons: ['next', 'close'],
          onNextClick: () => {
            if (incidentId) navigate(`/incidents/${incidentId}`);
            setTimeout(() => driverObj.moveNext(), 300);
          }
        }
      },
      {
        element: '.incident-submit-button',
        popover: {
          title: submitTitle,
          description: 'The app checks that everything is ready, then submits it for you.',
          showButtons: ['next', 'close'],
          onNextClick: () => {
            // Head to the Alice screen so the final step can highlight the message box.
            if (aliceEnabled) navigate('/alice');
            setTimeout(() => driverObj.moveNext(), 300);
          }
        }
      },
      // Meet Alice — only when the AI assistant is available to this user.
      // Highlights the message box + send button on the Alice screen itself.
      ...(aliceEnabled ? [{
        element: '.alice-index__input-wrapper',
        popover: {
          title: 'Meet Alice, your assistant',
          description: 'Have a question about a bill or your reimbursement? Type it here and Alice will help anytime.',
          side: 'top' as const,
          align: 'start' as const,
          showButtons: ['next', 'close'] as ('next' | 'close')[],
        }
      }] : [])
    ]
  });

  return driverObj;
}

export function useTour() {
  const navigate = useNavigate();
  const driverObj = useMemo(() => createDriver(navigate), []);

  // The tour's first step anchors on the Expenses draft empty-state CTA, so make
  // sure we're on that screen — and that the (async-gated) anchor has actually
  // rendered — before driving, since drive() highlights the first element
  // immediately. A fixed delay would race the /backend/plaid/items fetch that
  // gates the CTA; waiting for the element handles a cold backend, and the
  // capped wait still starts the tour if the anchor never appears.
  const start = useCallback(() => {
    navigate('/expenses');
    waitForElement(FIRST_STEP_ANCHOR).then(() => driverObj.drive());
  }, [driverObj, navigate])

  useEffect(() => {
    const startTour = localStorage.getItem('startTour')
    if (present(startTour)) {
      localStorage.removeItem('startTour');
      start();
    }
  }, [start])

  return { start }
}
